import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApi } from '../hooks/useApi';
import type { ConversationOut, ConversationSummary } from '../types/api';

const APP_ID = (import.meta.env.VITE_APP_ID as string | undefined) ?? 'demo';

interface ConversationsContextValue {
  conversations: ConversationSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (title?: string | null) => Promise<ConversationOut>;
  upsertSummary: (s: ConversationSummary) => void;
}

const ConversationsContext = createContext<ConversationsContextValue | null>(null);

export function ConversationsProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listConversations(false);
      // Sort newest first by updated_at — backend already does this, but be defensive.
      list.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
      setConversations(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (title?: string | null) => {
      const conv = await api.createConversation({ app_id: APP_ID, title: title ?? null });
      const summary: ConversationSummary = {
        id: conv.id,
        title: conv.title,
        app_id: conv.app_id,
        archived_at: conv.archived_at,
        created_at: conv.created_at,
        updated_at: conv.updated_at,
      };
      setConversations((prev) => [summary, ...prev]);
      return conv;
    },
    [api],
  );

  const upsertSummary = useCallback((s: ConversationSummary) => {
    setConversations((prev) => {
      const idx = prev.findIndex((c) => c.id === s.id);
      if (idx === -1) return [s, ...prev];
      const next = [...prev];
      next[idx] = s;
      // Re-sort
      next.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
      return next;
    });
  }, []);

  const value = useMemo<ConversationsContextValue>(
    () => ({ conversations, loading, error, refresh, create, upsertSummary }),
    [conversations, loading, error, refresh, create, upsertSummary],
  );

  return (
    <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>
  );
}

export function useConversations(): ConversationsContextValue {
  const v = useContext(ConversationsContext);
  if (!v) throw new Error('useConversations must be used inside ConversationsProvider');
  return v;
}

export { APP_ID };
